'use client';

import React, { useCallback, useEffect, useState } from 'react';
import { getImageProxyUrl } from '@/shared/lib/proxy-url';
import { Loading } from '@ncai-axe/react-ui-v3';
import { cn } from '@ncai-axe/react-ui-v3';
import ImageFillIcon from '@ncai-axe/react-ui-v3/assets/svg/icons/ic_image_fill.svg';
import styles from './styles.module.scss';
import { ProxyImage } from './ProxyImage';
import type { ImageThumbProps } from './types';
export function ImageThumb({
  src,
  alt = '',
  className,
  isError: externalIsError,
  isLoading: externalIsLoading,
  useProxy = true,
  style,
  onLoad,
  onError,
  useLoader = true,
  thumbnailSize,
  sizes,
  isSelected = false,
  isDisabled = false,
  onClick,
  hoverContent,
  selectionContent,
  badges,
  isBadgeInteractive,
  draggable = false,
  errorIcon,
  onDragStart,
  onDragEnd
}: ImageThumbProps) {
  const [internalStatus, setInternalStatus] = useState<'loading' | 'loaded' | 'error'>('loading');

  // src가 빈 문자열인지 확인
  const isEmptySrc = !src || src === '';

  // 외부 로딩 상태가 있으면 그것을 우선, 아니면 내부 상태 사용
  // src가 비어있으면 로딩 상태를 표시하지 않음
  const isImageLoading = externalIsLoading ? true : !isEmptySrc && internalStatus === 'loading';
  const isImageError = !isEmptySrc && (externalIsError ?? internalStatus === 'error');
  const handleImageError = useCallback(() => {
    setInternalStatus('error');
    onError?.();
  }, [onError]);
  const handleImageLoad = useCallback(() => {
    setInternalStatus('loaded');
    onLoad?.();
  }, [onLoad]);
  const handleContainerClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (isDisabled) return;
    onClick?.(e);
  }, [isDisabled, onClick]);
  const handleKeyDown = useCallback((e_0: React.KeyboardEvent<HTMLDivElement>) => {
    if (!onClick) return;
    if (e_0.target !== e_0.currentTarget) return;
    if (e_0.key === 'Enter' || e_0.key === ' ') {
      e_0.preventDefault();
      onClick(e_0 as unknown as React.MouseEvent<HTMLDivElement>);
    }
  }, [onClick]);
  useEffect(() => {
    if (!src) {
      setInternalStatus('error');
      return;
    }
    let cancelled = false;
    const img = new window.Image();
    const markLoaded = () => {
      if (cancelled) return;
      setInternalStatus('loaded');
    };
    const markError = () => {
      if (cancelled) return;
      setInternalStatus('error');
    };
    setInternalStatus('loading');

    // useProxy가 true이고 thumbnailSize가 있으면 프록시 URL 사용
    const imageUrl = useProxy ? getImageProxyUrl({
      src,
      thumbnailSize
    }) : src;
    img.onload = markLoaded;
    img.onerror = markError;
    img.src = imageUrl;

    // 캐시된 이미지 대응
    if (img.complete) {
      if (img.naturalWidth > 0) {
        markLoaded();
      } else {
        markError();
      }
    }
    return () => {
      cancelled = true;
      img.onload = null;
      img.onerror = null;
    };
  }, [src, useProxy, thumbnailSize]);
  return <div draggable={draggable} onDragStart={onDragStart} onDragEnd={onDragEnd} className={cn(styles.imageThumb, className, isSelected && styles.selected)} style={{
    width: style.width,
    height: style.height,
    borderRadius: style.borderRadius,
    ['--dimmed-color' as string]: onClick ? style.dimmedColor ?? 'var(--dimmed-tertiary, rgba(0, 0, 0, 0.12))' : 'transparent',
    cursor: onClick ? undefined : 'default'
  }} role={onClick ? 'button' : undefined} tabIndex={onClick ? 0 : undefined} aria-label={alt || undefined} onClick={handleContainerClick} onKeyDown={handleKeyDown}>
      {/* src가 없을 때 placeholder 아이콘 */}
      {isEmptySrc && !isImageLoading && <div className={styles.centerStatus} aria-label="No image" style={{
      width: style.errorIconSize?.width ?? '24px',
      height: style.errorIconSize?.height ?? '20px',
      color: 'var(--label-quinary)'
    }}>
          <ImageFillIcon />
        </div>}

      {/* 이미지 로딩 상태 */}
      {isImageLoading && <div className={styles.centerStatus} aria-label="Loading" aria-busy>
          <Loading size={style.loaderSize ?? 20} />
        </div>}

      {/* 이미지 에러 상태 */}
      {isImageError && <div className={styles.centerStatus} aria-label="Error" style={{
      width: style.errorIconSize?.width ?? '24px',
      height: style.errorIconSize?.height ?? '20px',
      color: 'var(--label-quinary)'
    }}>
          {errorIcon ?? <ImageFillIcon />}
        </div>}

      {/* 이미지 로드 성공 */}
      {!isImageError && src && <ProxyImage draggable={draggable} className={cn(styles.image, isImageLoading && styles.hidden)} unoptimized={!useProxy} useLoader={useLoader} thumbnailSize={thumbnailSize} sizes={sizes} fill src={src || 'placeholder'} alt={alt} onError={handleImageError} onLoad={handleImageLoad} />}

      {/*  뱃지 */}
      {badges && <div className={cn(styles.badge, isBadgeInteractive ? styles.badgeHigh : styles.badgeLow)} id="image-thumb-badge" onClick={e_1 => isBadgeInteractive && e_1.stopPropagation()}>
          {badges}
        </div>}

      {/* 선택 표시 아이콘 (Selected Indicator) - 왼쪽 상단, 먼저 포커스 */}
      {selectionContent && <div className={styles.selectionMark} data-selected={isSelected} id="image-thumb-selection-content">
          {selectionContent}
        </div>}

      {/* 인터랙션 오버레이 (Dimmed + Hover Button) */}
      <div className={styles.overlayLayer} id="image-thumb-overlay-layer">
        {/* Selected 상태가 아닐 때만 Hover Content 노출 */}
        {!isSelected && hoverContent && <div className={styles.hoverContent} id="image-thumb-hover-content" onClick={e_2 => e_2.stopPropagation()}>
            {hoverContent}
          </div>}
      </div>
    </div>;
}
ImageThumb.displayName = 'ImageThumb';